import React, { useEffect, useState } from 'react';
import {
	cmToImperial,
	formatHeightDisplay,
	HEIGHT_FEET_OPTIONS,
	HEIGHT_INCHES_OPTIONS,
	imperialSelectionComplete,
	imperialToCm,
	parseHeightDisplay
} from '../../utils/heightDisplay';

const selectClass =
	'w-full min-w-0 max-w-full box-border rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2.5 text-base text-gray-900 dark:text-dark-text outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400';

const unitToggleClass = (active) =>
	`rounded-full px-3 py-1.5 text-sm font-medium border transition ${
		active
			? 'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-600'
			: 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300'
	}`;

export default function HeightInput({ id, value, onChange, inputClassName, hasError }) {
	const parsed = parseHeightDisplay(value);
	const [unit, setUnit] = useState(parsed.unit);
	const [feet, setFeet] = useState(parsed.feet);
	const [inches, setInches] = useState(parsed.inches);
	const [cm, setCm] = useState(parsed.cm);

	useEffect(() => {
		const trimmed = String(value || '').trim();
		if (!trimmed) return;

		const next = parseHeightDisplay(value);
		setUnit(next.unit);
		setFeet(next.feet);
		setInches(next.inches);
		setCm(next.cm);
	}, [value]);

	function applyDisplay(parts) {
		const display = formatHeightDisplay(parts);
		onChange(display);
	}

	function applyImperial(nextFeet, nextInches) {
		if (imperialSelectionComplete(nextFeet, nextInches)) {
			applyDisplay({ unit: 'imperial', feet: nextFeet, inches: nextInches, cm: '' });
		} else {
			onChange('');
		}
	}

	function switchUnit(nextUnit) {
		if (nextUnit === unit) return;

		if (nextUnit === 'metric') {
			const nextCm =
				cm && Number(cm) > 0
					? String(cm)
					: imperialSelectionComplete(feet, inches)
						? String(imperialToCm(feet, inches))
						: '';
			setUnit('metric');
			setCm(nextCm);
			applyDisplay({ unit: 'metric', feet, inches, cm: nextCm });
			return;
		}

		const converted =
			cm && Number(cm) > 0 ? cmToImperial(cm) : { feet, inches };
		setUnit('imperial');
		setFeet(converted.feet);
		setInches(converted.inches);
		applyImperial(converted.feet, converted.inches);
	}

	const borderClass = hasError
		? 'border-red-400 dark:border-red-500'
		: 'border-gray-300 dark:border-dark-border';

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap gap-2" role="group" aria-label="Height unit">
				<button type="button" className={unitToggleClass(unit === 'imperial')} onClick={() => switchUnit('imperial')}>
					Feet & inches
				</button>
				<button type="button" className={unitToggleClass(unit === 'metric')} onClick={() => switchUnit('metric')}>
					Centimeters
				</button>
			</div>

			{unit === 'imperial' ? (
				<div className="grid grid-cols-2 gap-3">
					<div>
						<label htmlFor={`${id}-feet`} className="sr-only">
							Feet
						</label>
						<select
							id={`${id}-feet`}
							className={`${selectClass} ${borderClass}`}
							value={feet === '' ? '' : feet}
							onChange={(e) => {
								const nextFeet = e.target.value === '' ? '' : Number(e.target.value);
								setFeet(nextFeet);
								applyImperial(nextFeet, inches);
							}}
						>
							<option value="">Select feet</option>
							{HEIGHT_FEET_OPTIONS.map((f) => (
								<option key={f} value={f}>
									{f} ft
								</option>
							))}
						</select>
					</div>
					<div>
						<label htmlFor={`${id}-inches`} className="sr-only">
							Inches
						</label>
						<select
							id={`${id}-inches`}
							className={`${selectClass} ${borderClass}`}
							value={inches === '' ? '' : inches}
							onChange={(e) => {
								const nextInches = e.target.value === '' ? '' : Number(e.target.value);
								setInches(nextInches);
								applyImperial(feet, nextInches);
							}}
						>
							<option value="">Select inches</option>
							{HEIGHT_INCHES_OPTIONS.map((i) => (
								<option key={i} value={i}>
									{i} in
								</option>
							))}
						</select>
					</div>
				</div>
			) : (
				<div>
					<label htmlFor={`${id}-cm`} className="sr-only">
						Height in centimeters
					</label>
					<input
						id={`${id}-cm`}
						type="number"
						inputMode="numeric"
						min={100}
						max={250}
						step={1}
						placeholder="178"
						className={inputClassName}
						value={cm}
						onChange={(e) => {
							const nextCm = e.target.value;
							setCm(nextCm);
							applyDisplay({ unit: 'metric', feet, inches, cm: nextCm });
						}}
					/>
				</div>
			)}
		</div>
	);
}
